import React, { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../api";
import { agentKeys } from "../queryKeys";

interface Source {
  id: string;
  agent_id: string;
  url: string;
  crawl: boolean;
  status: string;
  error_message: string;
  created_at: string;
  scraped_at: string | null;
}

export function SourcesList({ agentId }: { agentId: string }) {
  const queryClient = useQueryClient();
  const [url, setUrl] = useState("");
  const [crawl, setCrawl] = useState(false);
  const [error, setError] = useState("");
  const [expandedSourceId, setExpandedSourceId] = useState<string | null>(null);

  const { data: sources = [] } = useQuery({
    queryKey: agentKeys.sources(agentId),
    queryFn: () =>
      apiFetch<{ sources: Source[] }>(`/api/agents/${agentId}/sources`).then((r) => r.sources),
    refetchInterval: (query) => {
      const data = query.state.data as Source[] | undefined;
      return data?.some((s) => s.status === "scraping") ? 3000 : false;
    },
  });

  const { data: chunksForExpanded = [], isLoading: loadingChunks } = useQuery({
    queryKey: agentKeys.sourceChunks(agentId, expandedSourceId!),
    queryFn: () =>
      apiFetch<{ chunks: string[] }>(
        `/api/agents/${agentId}/sources/${expandedSourceId}/chunks`
      ).then((r) => r.chunks),
    enabled: !!expandedSourceId,
  });

  const addMutation = useMutation({
    mutationFn: ({ url: u, crawl: c }: { url: string; crawl: boolean }) =>
      apiFetch(`/api/agents/${agentId}/sources`, {
        method: "POST",
        body: JSON.stringify({ url: u, crawl: c }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agentKeys.sources(agentId) });
      setUrl("");
      setCrawl(false);
    },
    onError: (err: Error) => setError(err.message || "Failed to add source"),
  });

  const deleteMutation = useMutation({
    mutationFn: (sourceId: string) =>
      apiFetch(`/api/agents/${agentId}/sources/${sourceId}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agentKeys.sources(agentId) });
    },
    onError: (err: Error) => setError(err.message || "Delete failed"),
  });

  const rescrapeMutation = useMutation({
    mutationFn: (sourceId: string) =>
      apiFetch(`/api/agents/${agentId}/sources/${sourceId}/rescrape`, { method: "POST" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agentKeys.sources(agentId) });
    },
    onError: (err: Error) => setError(err.message || "Rescrape failed"),
  });

  const busyIds = useMemo(() => {
    const ids = new Set<string>();
    if (deleteMutation.isPending && deleteMutation.variables) ids.add(deleteMutation.variables);
    if (rescrapeMutation.isPending && rescrapeMutation.variables)
      ids.add(rescrapeMutation.variables);
    return ids;
  }, [
    deleteMutation.isPending,
    deleteMutation.variables,
    rescrapeMutation.isPending,
    rescrapeMutation.variables,
  ]);

  const adding = addMutation.isPending;

  function handleAdd() {
    const trimmed = url.trim();
    if (!trimmed) return;
    setError("");
    addMutation.mutate({ url: trimmed, crawl });
  }

  function handleDelete(sourceId: string) {
    if (!confirm("Delete this source and all its data?")) return;
    setError("");
    deleteMutation.mutate(sourceId);
  }

  function toggleChunks(sourceId: string) {
    if (expandedSourceId === sourceId) {
      setExpandedSourceId(null);
      return;
    }
    setExpandedSourceId(sourceId);
  }

  function handleRescrape(sourceId: string) {
    setError("");
    rescrapeMutation.mutate(sourceId);
  }

  const loadingChunksId = loadingChunks && expandedSourceId ? expandedSourceId : null;
  const chunksMap: Record<string, string[]> = {};
  if (expandedSourceId) chunksMap[expandedSourceId] = chunksForExpanded;

  const statusBadge = (status: string) => {
    const colors: Record<string, string> = {
      pending: "#f59e0b",
      scraping: "#3b82f6",
      ready: "#22c55e",
      error: "#ef4444",
    };
    return (
      <span
        className="source-badge"
        style={{ background: colors[status] || "#888" }}
      >
        {status}
      </span>
    );
  };

  return (
    <div className="sources-section">
      <h2>Knowledge Sources</h2>
      <p className="section-hint">
        Add website URLs to train your agent&apos;s knowledge base. Scraping usually takes 15–30 seconds
        (JS-heavy sites may take longer). Refresh the list if a source stays on &quot;scraping&quot;.
      </p>

      {error && <div className="auth-error">{error}</div>}

      <div className="source-add-row">
        <input
          type="url"
          value={url}
          onChange={e => setUrl(e.target.value)}
          placeholder="https://example.com/page"
          disabled={adding}
          onKeyDown={e => e.key === "Enter" && handleAdd()}
        />
        <label className="source-crawl-label">
          <input
            type="checkbox"
            checked={crawl}
            onChange={e => setCrawl(e.target.checked)}
            disabled={adding}
          />
          Crawl subpages
        </label>
        <button className="btn btn-primary" onClick={handleAdd} disabled={adding || !url.trim()}>
          {adding ? "Adding..." : "Add URL"}
        </button>
      </div>

      {sources.length === 0 ? (
        <p className="dash-empty">No sources yet. Add a URL above to get started.</p>
      ) : (
        <div className="sources-list">
          {sources.map(s => (
            <div key={s.id} className="source-item">
              <div className="source-row">
                <div className="source-info">
                  <span className="source-url">{s.url}</span>
                  {s.crawl && <span className="source-badge" style={{ background: "#6366f1" }}>crawl</span>}
                  {statusBadge(s.status)}
                  {s.error_message && (
                    <span className="source-error" title={s.error_message}>
                      {s.error_message.slice(0, 80)}
                    </span>
                  )}
                </div>
                <div className="source-actions">
                  {s.status === "ready" && (
                    <button
                      type="button"
                      className="btn btn-sm source-expand-btn"
                      onClick={() => toggleChunks(s.id)}
                      title="View scraped data (raw chunks)"
                    >
                      {expandedSourceId === s.id ? "▼ Hide" : "▶ View scraped"}
                    </button>
                  )}
                  <button
                    className="btn btn-sm"
                    onClick={() => handleRescrape(s.id)}
                    disabled={busyIds.has(s.id)}
                  >
                    Re-scrape
                  </button>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => handleDelete(s.id)}
                    disabled={busyIds.has(s.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>
              {expandedSourceId === s.id && (
                <div className="source-chunks">
                  {loadingChunksId === s.id ? (
                    <p className="dash-empty">Loading...</p>
                  ) : (chunksMap[s.id]?.length ?? 0) > 0 ? (
                    <div className="source-chunks-content">
                      {(chunksMap[s.id] ?? []).map((content, i) => (
                        <div key={i} className="source-chunk-block">
                          <span className="source-chunk-label">Chunk {i + 1}</span>
                          <pre className="source-chunk-text">{content}</pre>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="dash-empty">No chunks (not scraped yet or source failed).</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
